import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { SFEntityID, SFNotification, StaffNotificationPreferences, StaffProfileData, StaffProfilePrivileges, StaffAppointmentPreferences } from 'utils/types';
import ProfileDetails from './ProfileDetails';
import NotificationDetails from './NotificationDetails';
import AppointmentPreferenceDetails from './AppointmentPreferenceDetails';
import Profile from '../Profile';
import { findIndex } from 'lodash';
import { isEmptyString, isValidEmail } from 'utils/text';
import { hasInvalidSchedulingDeadline, isValidStaffTitle } from '../profileValidation';
import { getWord } from 'utils/tenant';

/**
 * @param {object} props - see propTypes
 * @returns {JSX.Element} the tabs for the staff's profile
 */
const StaffProfileContainer = (props) => {
    const getTabs = () => {
        const tabs = [{
            id: 'staffEditProfileTab',
            name: 'Edit Profile'
        }];

        if (props.privileges.showAppointmentPreferencesTab) {
            tabs.push({
                id: 'staffAppointmentsTab',
                name: 'Appointment Preferences'
            });
        }

        tabs.push({
            id: 'staffNotificationsTab',
            name: 'Notifications'
        });

        return tabs;
    };

    const getTabIndex = (tabId) => {
        const tabIndex = findIndex(getTabs(), tab => tab.id === tabId);
        return tabIndex > -1 ? tabIndex : 0;
    };

    const getInitialTabs = () => {
        const searchParams = new URLSearchParams(window.location.search);
        switch (searchParams.get('tabRequest')) {
            case 'Institutional Profile':
                return getTabIndex('staffEditProfileTab');
            case 'Appointment Preferences':
                return getTabIndex('staffAppointmentsTab');
            case 'Email Notifications':
                return getTabIndex('staffNotificationsTab');
            default:
                return 0;
        }
    };

    const [selectedTab, selectTab] = useState(getInitialTabs());

    // Scroll to top when selected tab changes
    useEffect(() => {
        props.contentRef.current.scrollTop = 0;
    }, [selectedTab]);

    useEffect(() => {
        const noEmailNotification = isEmptyString(props.profile.correspondenceTo);
        const invalidAlternateEmail = !isEmptyString(props.profile.preferredEmail) && !isValidEmail(props.profile.preferredEmail);
        const invalidSchedulingDeadline = hasInvalidSchedulingDeadline(props.appointmentPreferences);
        const errors = [
            noEmailNotification && 'You must have at least one email address selected in your Profile to receive email notifications.',
            invalidAlternateEmail && `Please provide a valid ${getWord('alternateEmail', true)} address.`,
            !isValidStaffTitle(props.profile.title) && 'Title field cannot contain more than 140 characters.',
             props.invalidPronouns && 'Pronouns field cannot contain more than 25 characters.',
            invalidSchedulingDeadline && `Please provide a valid scheduling deadline for your ${getWord('officeHours')}.`
        ].filter(Boolean);

        const errorMessage = errors.length > 1 ? 'You have more than one error on the fields below.' : errors[0];
        if (errorMessage) {
            props.notification.error(errorMessage);
        } else {
            props.notification.dismiss();
        }
    }, [props.invalidPronouns, props.profile.correspondenceTo, props.profile.preferredEmail, props.profile.title, props.appointmentPreferences]);

    return (
        <Profile notification={props.notification}
                 onSelect={tabIndex => selectTab(tabIndex)}
                 selectedTab={selectedTab}
                 tabs={getTabs()}>
            <ProfileDetails {...props}
                            goToNotificationsTab={() => selectTab(getTabIndex('staffNotificationsTab'))} />
            {
                props.privileges.showAppointmentPreferencesTab &&
                <AppointmentPreferenceDetails {...props} />
            }
            <NotificationDetails goToProfileDetails={() => selectTab(0)}
                                 privileges={props.privileges}
                                 profile={props.profile}
                                 notificationPreferences={props.notificationPreferences}
                                 setValue={props.setValue}
                                 supportedCountries={props.supportedCountries}
                                 userId={props.userId} />
        </Profile>
    );
};

StaffProfileContainer.propTypes = {
    appointmentPreferences: StaffAppointmentPreferences,
    contentRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any })
    ]),
    exchangeAccounts: PropTypes.string,
    invalidPronouns: PropTypes.bool,
    isIE: PropTypes.bool,
    mobileEmailInfoLink: PropTypes.string,
    notification: SFNotification.isRequired,
    notificationPreferences: StaffNotificationPreferences,
    originalProfile: StaffProfileData.isRequired,
    photoAcceptTypes: PropTypes.string,
    privileges: StaffProfilePrivileges,
    profile: StaffProfileData.isRequired,
    scheduleId: PropTypes.number,
    setValue: PropTypes.func.isRequired,
    supportedCountries: PropTypes.string,
    userId: SFEntityID.isRequired,
    externalCalendarPreferenceSync: PropTypes.func.isRequired,
    validatePhoto: PropTypes.func.isRequired
};

export default StaffProfileContainer;
